import React, { useEffect } from "react";
import { useDelayedPayments } from "api/hooks/useDelayedPayments";
import { useCoursesOptions } from "api/hooks";
import { useUsersOptions } from "api/hooks/useUsers";
import { PageMain } from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { routes } from "consts";
import { IDropdownOption, PaziaLoader, PaziaButton } from "components/atoms";
import {
  TanTable,
  TanTableColumn,
  useFilters,
  useInitFilters,
  usePagination,
  useSorting,
} from "components/molecules";
import { formatDate } from "utils";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS, REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS } from "consts";
import { Link, useNavigate } from "react-router-dom";
import { DelayedPaymentApi } from "api/payments/paymentsApi";

const FILTER_NAME = "delayed-payments-filter";

export const DelayedPaymentsPage = () => {
  const { getInitFilterValue, getInitFilterValues } = useInitFilters(FILTER_NAME);
  const { isLoading: loadingUsers, options: usersOptions } = useUsersOptions();
  const { isLoading: loadingCourses, options: coursesOptions } = useCoursesOptions();

  if (loadingUsers || loadingCourses) return <PaziaLoader position="center" />;

  return (
    <DelayedPaymentsPageInner
      initValues={{
        userId: getInitFilterValue("userId", "like") ?? "",
        courseId: getInitFilterValue("courseId", "like") ?? "",
        status: getInitFilterValues("status"),
      }}
      usersOptions={usersOptions}
      coursesOptions={coursesOptions}
    />
  );
};

interface DelayedPaymentsPageInnerProps {
  initValues: {
    userId: string;
    courseId: string;
    status: number[];
  };
  usersOptions: IDropdownOption[];
  coursesOptions: IDropdownOption[];
}

export const DelayedPaymentsPageInner = ({
  initValues,
  usersOptions,
  coursesOptions,
}: DelayedPaymentsPageInnerProps) => {
  const navigate = useNavigate();
  const { searchPhrase, filter, updateAllFilters } = useFilters(FILTER_NAME);
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting("dueDate", "ASC");

  const { isFetching, isLoading, invalidate, delayedPayments, total } = useDelayedPayments(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const columns: TanTableColumn<DelayedPaymentApi>[] = [
    {
      id: "userId",
      accessorKey: "userId",
      size: 10,
      header: "ID Użytkownika",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => (
        <Link
          target="_blank"
          to={routes.STUDENT_NESTED.PAYMENTS(row.row.original.userId, row.row.original.courseId)}
          className="hover:underline"
        >
          {row.row.original.userId}
        </Link>
      ),
    },
    {
      id: "userFullName",
      accessorKey: "userFullName",
      header: "Użytkownik",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => (
        <Link
          target="_blank"
          to={routes.STUDENT_NESTED.PAYMENTS(row.row.original.userId, row.row.original.courseId)}
          className="hover:underline"
        >
          {row.row.original.userFullName}
        </Link>
      ),
    },
    {
      id: "userEmail",
      accessorKey: "userEmail",
      header: "Email",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: "userPhone",
      accessorKey: "userPhone",
      header: "Telefon",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: "courseId",
      accessorKey: "courseId",
      header: "Kurs",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => (
        <Link className="cursor-pointer hover:underline" to={routes.COURSES_MANAGE(row.row.original.courseId)}>
          {row.row.original.courseName}
        </Link>
      ),
    },
    {
      id: "trancheName",
      accessorKey: "trancheName",
      header: "Transza",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => row.row.original.trancheName || <span className="text-slate-300">-</span>,
    },
    {
      id: "trancheAmount",
      accessorKey: "trancheAmount",
      header: "Kwota",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => `${row.row.original.trancheAmount} PLN`,
    },
    {
      id: "discountedAmount",
      accessorKey: "discountedAmount",
      header: "Kwota po zniżce",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => `${row.row.original.discountedAmount} PLN`,
    },
    {
      id: "dueDate",
      accessorKey: "dueDate",
      header: "Termin płatności",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => formatDate(row.row.original.dueDate, true, false),
      meta: {
        filterVariant: "date-range",
      },
    },
    {
      id: "daysOverdue",
      accessorKey: "daysOverdue",
      header: "Dni opóźnienia",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => (
        <span className={row.row.original.daysOverdue > 30 ? "font-semibold text-red-600" : "text-orange-600"}>
          {row.row.original.daysOverdue}
        </span>
      ),
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) =>
        REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[
          row.row.original.status as keyof typeof REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS
        ],
    },
    {
      id: "registrationCreatedDate",
      accessorKey: "registrationCreatedDate",
      header: "Data rejestracji",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => formatDate(row.row.original.registrationCreatedDate, true, false),
    },
    {
      id: "buyer",
      accessorKey: "buyer",
      header: "Nabywca",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => row.row.original.buyer || <span className="text-slate-300">-</span>,
    },
    {
      id: "lastReminderSentTime",
      accessorKey: "lastReminderSentTime",
      header: "Ostatnie przypomnienie",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) =>
        row.row.original.lastReminderSentTime ? (
          formatDate(row.row.original.lastReminderSentTime, true, true)
        ) : (
          <span className="text-slate-300">Brak</span>
        ),
    },
  ];

  return (
    <PageMain>
      <PageRichHeader header="Opóźnione płatności">
        <div className="flex grow justify-end">
          <PaziaButton type="tertiary" title="Wróć do finansów" onClick={() => navigate(routes.FINANCES)} />
        </div>
      </PageRichHeader>

      <TanTable
        data={delayedPayments}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        initialFilters={[
          {
            id: "userId",
            value: initValues.userId,
          },
          {
            id: "courseId",
            value: initValues.courseId,
          },
          {
            id: "status",
            value: initValues.status,
          },
        ]}
      />
    </PageMain>
  );
};
